// src/pages/admin/AnalyticsPage.jsx
import { useQuery } from '@tanstack/react-query'
import { adminAnalyticsApi } from '../../api/services'
import { Card, CardHeader, CardBody, PageSpinner } from '../../components/common/ui'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts'
import {
  TrendingUp, TrendingDown, ShoppingBag, Users, DollarSign, Package,
  AlertTriangle, Clock, Scale, Shield, Star, Repeat, Truck, CreditCard,
} from 'lucide-react'

const COLORS = ['#FF3D7F', '#FF8500', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6']

const fmt = v => `₹${Number(v || 0).toLocaleString('en-IN')}`
const fmtK = v => v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${(v / 1000).toFixed(0)}K` : fmt(v)

export default function AdminAnalyticsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics-full'],
    queryFn: () => adminAnalyticsApi.index(),
    refetchInterval: 120000, // refresh every 2 min
  })

  if (isLoading) return <PageSpinner />

  const d = data?.data?.data || {}
  const overview = d.overview || {}
  const earnings = d.earnings || {}
  const health = d.health || {}
  const users = d.users || {}

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-display font-bold">Platform Analytics</h1>

      {/* ═══════════════════════════════════════════════════════════
          KPI CARDS — Row 1: Revenue
          ══════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} color="green" label="GMV This Month"
          value={fmt(overview.gmv_month)} sub={overview.gmv_growth_rate != null ? `${overview.gmv_growth_rate > 0 ? '+' : ''}${overview.gmv_growth_rate}% vs last month` : null}
          trend={overview.gmv_growth_rate} />
        <KpiCard icon={ShoppingBag} color="blue" label="Orders This Month"
          value={overview.orders_month || 0} sub={`${overview.orders_today || 0} today`} />
        <KpiCard icon={DollarSign} color="purple" label="Avg Order Value"
          value={fmt(overview.avg_order_value)} />
        <KpiCard icon={Repeat} color="amber" label="Repeat Buyer Rate"
          value={`${overview.repeat_buyer_rate || 0}%`} sub={`${overview.active_buyers || 0} new buyers this month`} />
      </div>

      {/* ═══════════════════════════════════════════════════════════
          KPI CARDS — Row 2: Platform Health
          ══════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={CreditCard} color="green" label="Commission Earned"
          value={fmt(earnings.total_commission)} sub={`GMV: ${fmt(earnings.total_gmv)}`} />
        <KpiCard icon={AlertTriangle}
          color={health.sla_breach_rate > 10 ? 'red' : 'green'}
          label="SLA Breach Rate"
          value={`${health.sla_breach_rate || 0}%`}
          sub={health.avg_delivery_days ? `Avg delivery: ${health.avg_delivery_days}d` : null} />
        <KpiCard icon={Scale} color={health.open_disputes > 0 ? 'amber' : 'gray'} label="Open Disputes"
          value={health.open_disputes || 0} sub={`${health.open_fraud_flags || 0} fraud flags`} />
        <KpiCard icon={Truck} color="blue" label="Pending Payouts"
          value={fmt(health.pending_payouts)} sub={`${health.pending_payout_count || 0} suppliers`} />
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CHARTS — Row 1: GMV Trend + Daily Revenue
          ══════════════════════════════════════════════════════════ */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Monthly GMV trend */}
        {d.gmv_trend?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">GMV Trend (12 Months)</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={d.gmv_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                  <Tooltip formatter={v => [fmt(v), 'GMV']} />
                  <Bar dataKey="gmv" fill="#FF3D7F" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}

        {/* Daily revenue (last 30 days) */}
        {d.daily_revenue?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Daily Revenue (30 Days)</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={d.daily_revenue}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                  <Tooltip formatter={(v, name) => [name === 'revenue' ? fmt(v) : v, name === 'revenue' ? 'Revenue' : 'Orders']} />
                  <Area type="monotone" dataKey="revenue" stroke="#6366F1" fill="url(#revGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CHARTS — Row 2: Order Status + Category Revenue
          ══════════════════════════════════════════════════════════ */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Order status breakdown */}
        {Object.keys(d.status_breakdown || {}).length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Orders by Status</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={Object.entries(d.status_breakdown).map(([status, count]) => ({ name: status.replace(/_/g, ' '), value: count }))}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {Object.keys(d.status_breakdown).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}

        {/* Top categories */}
        {d.top_categories?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Revenue by Category</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={d.top_categories} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                  <Tooltip formatter={v => [fmt(v), 'Revenue']} />
                  <Bar dataKey="revenue" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          CHARTS — Row 3: Buyer Cohort + Fabric Types
          ══════════════════════════════════════════════════════════ */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Buyer growth */}
        {d.buyer_cohort?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">New Buyer Registrations</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={d.buyer_cohort}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="New Buyers" />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}

        {/* Fabric type popularity */}
        {d.fabric_types?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Fabric Type Popularity (Metres Sold)</span></CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={d.fabric_types} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="fabric_type" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip formatter={v => [`${Number(v).toLocaleString('en-IN')}m`, 'Metres Sold']} />
                  <Bar dataKey="total_metres" fill="#F59E0B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          LEADERBOARDS — Top Suppliers + Top Products
          ══════════════════════════════════════════════════════════ */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top Suppliers */}
        {d.top_suppliers?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Top 10 Suppliers by GMV</span></CardHeader>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface">
                    <th className="text-left px-4 py-2 text-[10px] font-bold text-muted uppercase">#</th>
                    <th className="text-left px-4 py-2 text-[10px] font-bold text-muted uppercase">Supplier</th>
                    <th className="text-right px-4 py-2 text-[10px] font-bold text-muted uppercase">GMV</th>
                    <th className="text-right px-4 py-2 text-[10px] font-bold text-muted uppercase">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_suppliers.map((s, i) => (
                    <tr key={s.supplier_id || i} className="border-b border-border last:border-0 hover:bg-surface/50">
                      <td className="px-4 py-2.5 font-bold text-muted">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium">{s.name || s.supplier_name}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-green-600">{fmt(s.gmv)}</td>
                      <td className="px-4 py-2.5 text-right text-muted">{s.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        {/* Top Products */}
        {d.top_products?.length > 0 && (
          <Card>
            <CardHeader><span className="font-bold text-sm">Top 10 Products by Revenue</span></CardHeader>
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface">
                    <th className="text-left px-4 py-2 text-[10px] font-bold text-muted uppercase">#</th>
                    <th className="text-left px-4 py-2 text-[10px] font-bold text-muted uppercase">Product</th>
                    <th className="text-right px-4 py-2 text-[10px] font-bold text-muted uppercase">Revenue</th>
                    <th className="text-right px-4 py-2 text-[10px] font-bold text-muted uppercase">Metres</th>
                  </tr>
                </thead>
                <tbody>
                  {d.top_products.map((p, i) => (
                    <tr key={p.product_id || i} className="border-b border-border last:border-0 hover:bg-surface/50">
                      <td className="px-4 py-2.5 font-bold text-muted">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium truncate max-w-[200px]">{p.product_name || p.name}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-blue-600">{fmt(p.revenue)}</td>
                      <td className="px-4 py-2.5 text-right text-muted">{Number(p.metres || p.units_sold || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          USER STATS + EARNINGS BREAKDOWN
          ══════════════════════════════════════════════════════════ */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* User stats */}
        <Card>
          <CardHeader><span className="font-bold text-sm">User Base</span></CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <StatBlock label="Total Buyers" value={users.total_buyers || 0} sub={`+${users.new_buyers_month || 0} this month`} />
              <StatBlock label="Total Suppliers" value={users.total_suppliers || 0} sub={`${users.approved_suppliers || 0} approved`} />
              <StatBlock label="Products Live" value={overview.products_live || 0} sub={`${health.pending_reviews || 0} pending review`} />
              <StatBlock label="New Suppliers" value={users.new_suppliers_month || 0} sub="this month" />
            </div>
          </CardBody>
        </Card>

        {/* Earnings breakdown */}
        <Card>
          <CardHeader><span className="font-bold text-sm">Earnings Breakdown (This Month)</span></CardHeader>
          <CardBody>
            <div className="space-y-3">
              {[
                { label: 'Total GMV',          value: fmt(earnings.total_gmv),               color: 'text-gray-900' },
                { label: 'Platform Commission', value: fmt(earnings.total_commission),        color: 'text-green-600' },
                { label: 'TDS Collected',       value: fmt(earnings.total_tds),               color: 'text-amber-600' },
                { label: 'Supplier Earnings',   value: fmt(earnings.total_supplier_earnings), color: 'text-blue-600' },
                { label: 'Pending Payouts',     value: fmt(earnings.pending_payouts),         color: 'text-purple-600' },
                { label: 'Completed Payouts',   value: fmt(earnings.completed_payouts),       color: 'text-green-700' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted">{row.label}</span>
                  <span className={`font-bold ${row.color}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Quick links */}
      <div className="bg-surface rounded-xl p-4 text-center text-xs text-muted">
        GMV today: <strong className="text-gray-900">{fmt(overview.gmv_today)}</strong> · Orders today: <strong className="text-gray-900">{overview.orders_today || 0}</strong>
      </div>
    </div>
  )
}

/* ── Reusable KPI Card ────────────────────────────────────────────── */
function KpiCard({ icon: Icon, color, label, value, sub, trend }) {
  const colorMap = {
    green: 'bg-green-50 text-green-600 border-green-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    gray: 'bg-gray-50 text-gray-500 border-gray-200',
  }
  const classes = colorMap[color] || colorMap.gray

  return (
    <div className={`border rounded-2xl p-4 ${classes.split(' ').filter(c => c.startsWith('bg-') || c.startsWith('border-')).join(' ')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={classes.split(' ').find(c => c.startsWith('text-'))} />
        <span className="text-[10px] font-bold text-muted uppercase">{label}</span>
        {trend != null && (
          <span className={`ml-auto text-[10px] font-bold flex items-center gap-0.5 ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className={`text-xl font-extrabold ${classes.split(' ').find(c => c.startsWith('text-'))}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted mt-0.5">{sub}</p>}
    </div>
  )
}

/* ── Reusable Stat Block ──────────────────────────────────────────── */
function StatBlock({ label, value, sub }) {
  return (
    <div className="bg-surface rounded-xl p-3 text-center">
      <p className="text-xl font-extrabold text-gray-900">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</p>
      <p className="text-[10px] font-bold text-muted uppercase mt-1">{label}</p>
      {sub && <p className="text-[10px] text-muted">{sub}</p>}
    </div>
  )
}
